import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import PageHeader from 'components/general/pageHeader';
import SortFilter from 'components/general/sortFilter';
import NoResults from 'components/general/noResults';
import CompetencyList from 'components/general/competencyList';
import Rating from 'components/general/rating';
import FileLink from 'components/general/fileLink';
import Comments from './components/comments';
import { extension } from 'components/general/assets';
import { apiGet } from '../../services';

import { DomHelpers, Forms, Loading, Paging, SystemText } from 'e4shared';
import queryString from 'query-string';

import './pitchback.css';
import { e4Format, e4Formats } from 'helpers/dates';

export const Pitchback = ({ history }) => {
    const pageSize = 12;
    const [page, setPage] = React.useState(1);
    const [recordCount, setRecordCount] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [task, setTask] = React.useState(null);
    const [person, setPerson] = React.useState(null);
    const [competency, setCompetency] = React.useState(null);
    const [showComments, setShowComments] = React.useState(false);
    const [comments, setComments] = React.useState([]);
    const [pitchbackTasks, setPitchbackTasks] = React.useState([]);
    const [peopleMapped, setPeopleMapped] = React.useState([]);
    const [competenciesMapped, setCompetenciesMapped] = React.useState([]);
    const [tasksMapped, setTasksMapped] = React.useState([]);

    const guid = useSelector(state => (state.user.user ? state.user.user.guid : ''));
    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);

    const values = queryString.parse(history.location.search);
    const id = parseInt(values.id, 10) || 0;

    const resizeHandler = () => setTimeout(DomHelpers.arrangeTiles('post-card'), 250);

    const st = (path, ...rest) => SystemText(`Pitchback/${path}`, ...rest);

    React.useEffect(() => {
        window.addEventListener('resize', resizeHandler);
        resizeHandler();

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);

    React.useEffect(() => {
        setPage(1);
    }, [task, person, competency]);

    React.useEffect(() => {
        setLoading(true);
        apiGet(
            `pitchback-tasks?page=${page}&pagesize=${pageSize}&task=${task}&person=${person}&competency=${competency}&id=${id}`,
            guid,
            response => {
                setPitchbackTasks(response.results);
                setPeopleMapped(response.people);
                setCompetenciesMapped(response.competencies);
                setTasksMapped(response.tasks);
                setRecordCount(response.records);
                setLoading(false);
            }
        );
    }, [guid, id, page, task, person, competency]);

    React.useEffect(() => {
        setTimeout(() => resizeHandler(), 500);
    }, [leftAsideOpen, pitchbackTasks, loading, page]);

    const card = task => {
        if ((task.feedback_1 || task.feeback_2 || task.feedback_3) && !task.share_Comments?.some(c => c.id === 0))
            task.share_Comments.unshift({
                id: 0,
                owner_Id: task.owner_Id,
                owner_Full_Name: task.owner_Fullname,
                owner_Avatar: task.owner_Avatar,
                comment: st('Comment', task.feedback_1, task.feedback_2, task.feedback_3),
                date_Added: null,
            });
        if (task.comments && !task.share_Comments?.some(c => c.id === -1))
            task.share_Comments?.unshift({
                id: -1,
                owner_Id: task.profile_Id,
                owner_Full_Name: task.assigned_Fullname,
                owner_Avatar: task.assigned_Avatar,
                comment: task.comments,
                date_Added: task.date_Completed,
            });
        const commentCount = task.share_Comments?.length || 0;
        return (
            <div key={`${task.id}-${task.profile_Id}`} className="box-container col-xs-12 col-sm-6 col-md-4 col-lg-3 mb20">
                <div className="box post-card">
                    <div className="">
                        <div className="video-holder">
                            <div className="video-inner">
                                <FileLink
                                    onLoaded={resizeHandler}
                                    key={`${task.profile_Id}-${task.id}`}
                                    folder="task_Completion"
                                    file={{
                                        ...task.completed_Video_Asset,
                                        video: true,
                                        ext: extension(task.completed_Video_Asset),
                                    }}
                                />
                            </div>
                        </div>
                        <div className="mb5">
                            <h3>
                                <Link to={`/objectives/${task.profile_Id}/objective/${task.id}`}>{task.name}</Link>
                            </h3>
                        </div>
                        <div className="description mb10">
                            <div className="flex flex-center">
                                <i className="fa fa-user mr5" />
                                <Link to={`/search/${task.assigned_Fullname}`}>{task.assigned_Fullname}</Link>
                            </div>
                            <div className="flex flex-center">
                                <i className="fa fa-calendar mr5" />
                                {e4Format(task.date_Completed, e4Formats.long)}
                            </div>
                            {!task.publicAccessOnly && (
                                <div className="flex flex-center">
                                    <i className="fa fa-comment mr5" />
                                    {commentCount === 0 ? (
                                        <span> {st('Comments', commentCount)}</span>
                                    ) : (
                                        <button
                                            className="btn-link inline"
                                            data-testid="comments_Count"
                                            onClick={() => {
                                                setShowComments(true);
                                                setComments(task.share_Comments || []);
                                            }}
                                        >
                                            {st('Comments', commentCount)}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {task.rating > 0 && <Rating value={task.rating} hideLabel smallStar />}
                        <CompetencyList competencies={task.competencies} />
                    </div>
                </div>
            </div>
        );
    };

    const filterFields = () => (
        <div className="row">
            <div className="col-md-4">
                <Forms.Select
                    data-testid="select-task"
                    label={st('Fields/Objective')}
                    placeholder={st('Fields/Objective/Placeholder')}
                    data={tasksMapped}
                    value={task}
                    onChange={setTask}
                />
            </div>
            <div className="col-md-4">
                <Forms.Select
                    data-testid="select-person"
                    label={st('Fields/Person')}
                    placeholder={st('Fields/Person/Placeholder')}
                    data={peopleMapped}
                    value={person}
                    onChange={setPerson}
                />
            </div>
            <div className="col-md-4">
                <Forms.Select
                    data-testid="select-competency"
                    label={st('Fields/Competency')}
                    placeholder={st('Fields/Competency/Placeholder')}
                    data={competenciesMapped}
                    value={competency}
                    onChange={setCompetency}
                />
            </div>
        </div>
    );

    return (
        <div className="pitchbacks">
            <PageHeader title={st('Title')} />
            <section className="content mtm10" data-testid="pitchback-content">
                <SortFilter filterFields={filterFields} />
                <div className="row">
                    {loading || !pitchbackTasks ? (
                        <Loading data-testid="loading" />
                    ) : pitchbackTasks.length === 0 ? (
                        <div className="col-md-12">
                            <NoResults title={st('NoResults/Title')} message={st('NoResults/Message')} />
                        </div>
                    ) : (
                        <div className="col-md-12">
                            <div className="list row">
                                {recordCount === 0 ? (
                                    <div className="col-md-12">
                                        <NoResults
                                            title={st('NoSearchResults/Title')}
                                            message={st('NoSearchResults/Message')}
                                        />
                                    </div>
                                ) : (
                                    pitchbackTasks.map(t => card(t))
                                )}
                            </div>
                            {recordCount > 0 && (
                                <div className="text-center">
                                    <Paging
                                        data-testid="paging"
                                        pageSize={pageSize}
                                        recordCount={recordCount}
                                        page={page}
                                        goToPage={page => setPage(page)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
            <Comments show={showComments} comments={comments} close={() => setShowComments(false)} />
        </div>
    );
};

export default Pitchback;
